import { Tabs, useGlobalSearchParams } from 'expo-router';
import { Color } from '@/GlobalStyles';
import { Icon } from 'react-native-paper';
import { Platform, StyleSheet, View, Text, Pressable } from 'react-native';
import { Link } from 'expo-router';
import React, { useEffect } from 'react';
import { HrefObject } from 'expo-router/build/link/href';
import { usePatientContext } from '@/components/Contexts/PatientContext';
import { useTranslation } from 'react-i18next';
import CenteredLoading from '@/components/CenteredLoading';
import { useSafeAreaInsets } from '@/hooks/useSafeAreaInsets';

const getTabBarButton = (href, disabled = false) => {
  return (props) => {
    if (href == null) {
      return null;
    }
    if (disabled) {
      return (
        <View {...props} style={[{ display: 'flex' }, props.style]}>
          {props.children}
        </View>
      );
    }
    const children =
      Platform.OS === 'web' ? props.children : <Pressable>{props.children}</Pressable>;
    return (
      <Link
        {...props}
        disabled={disabled}
        style={[{ display: 'flex' }, props.style]}
        href={href}
        asChild={Platform.OS !== 'web'}
        children={children}
      />
    );
  };
};

const getOptions = (name: string, icon: string, href: HrefObject, disabled?: boolean) => {
  const inactiveColor = disabled ? Color.VertElectrique : Color.blanc;
  return {
    tabBarLabel: ({ focused, color, children }) => (
      <Text
        numberOfLines={1}
        style={[
          styles.label,
          { color: !focused ? inactiveColor : color },
          styles.labelBeneath,
          {
            fontSize: 16,
            marginBottom: 15,
          },
        ]}
        allowFontScaling={false}
      >
        {name}
      </Text>
    ),
    tabBarIcon: ({ focused, color }) => (
      <Icon size={20} source={icon} color={!focused ? inactiveColor : color} />
    ),
    tabBarButton: getTabBarButton(href, disabled),
  };
};

const TabLayout = () => {
  const { detail } = useGlobalSearchParams();
  const { t } = useTranslation();
  const { loading, patient, setPatientId } = usePatientContext();
  const { bottom } = useSafeAreaInsets();

  useEffect(() => {
    if (detail) {
      setPatientId(detail as string);
    }
  }, [detail]);

  if (loading) {
    return <CenteredLoading />;
  }

  const getDisabled = (routeName: string) => {
    switch (routeName) {
      case 'biometrie':
        return false;
      case 'habitudes':
        return (patient?.oeils || []).length === 0;
      case 'implants':
        return (patient?.habitudes || []).length === 0;
      case 'rapport':
        return (
          ((patient?.oeils || []).length > 0
            ? patient?.oeils[1]?.choixes || patient?.oeils[0].choixes || []
            : []
          ).length === 0
        );
      default:
        return true;
    }
  };

  return (
    <Tabs
      screenOptions={({ route }) => {
        const inactiveColor = getDisabled(route.name) ? Color.VertElectrique : Color.blanc;

        return {
          headerShown: false,
          tabBarActiveBackgroundColor: Color.VertElectrique,
          tabBarInactiveBackgroundColor: Color.MainBleu,
          tabBarInactiveTintColor: inactiveColor,
          tabBarActiveTintColor: Color.blanc,
          tabBarLabelPosition: 'below-icon',
          tabBarStyle: {
            height: 80 + bottom,
            backgroundColor: Color.MainBleu,
          },
        };
      }}
    >
      <Tabs.Screen
        name="biometrie"
        options={getOptions(
          t('Biométrie'),
          'eye',
          {
            pathname: '/patients/[detail]/biometrie',
            params: {
              detail,
            },
          },
          false
        )}
      />
      <Tabs.Screen
        name="habitudes"
        options={getOptions(
          t('Habitudes'),
          'view-day-outline',
          {
            pathname: '/patients/[detail]/habitudes',
            params: {
              detail,
            },
          },
          getDisabled('habitudes')
        )}
      />
      <Tabs.Screen
        name="implants"
        options={getOptions(
          t('Implants'),
          'camera-iris',
          {
            pathname: '/patients/[detail]/implants',
            params: {
              detail,
            },
          },
          getDisabled('implants')
        )}
      />
      <Tabs.Screen
        name="rapport"
        options={getOptions(
          t('Rapport'),
          'google-analytics',
          {
            pathname: '/patients/[detail]/rapport',
            params: {
              detail,
            },
          },
          getDisabled('rapport')
        )}
      />
    </Tabs>
  );
};

export default TabLayout;

const styles = StyleSheet.create({
  label: {
    textAlign: 'center',
    backgroundColor: 'transparent',
  },
  labelBeneath: {
    fontSize: 10,
  },
});
